import * as normalize from 'utils/normalize';

import getPatientFirstName from 'utils/print/getPatientFirstName';
import getPatientLastName from 'utils/print/getPatientLastName';
import getPatientFullDob from 'utils/print/getPatientFullDob';
import getPatientGender from 'utils/print/getPatientGender';
import getPatientHeightFtIn from 'utils/print/getPatientHeightFtIn';
import getPatientWeight from 'utils/print/getPatientWeight';
import getPatientFullAddress from 'utils/print/getPatientFullAddress';

import getBusinessName from 'utils/print/getBusinessName';
import getBusinessStreet from 'utils/print/getBusinessStreet';
import getBusinessCityStateZip from 'utils/print/getBusinessCityStateZip';

import getFacilityName from 'utils/print/getFacilityName';
import getFacilityStreet from 'utils/print/getFacilityStreet';
import getFacilityCityStateZip from 'utils/print/getFacilityCityStateZip';

import getInsuranceName from 'utils/print/getInsuranceName';
import getInsurancePlanAndType from 'utils/print/getInsurancePlanAndType';
import getInsuranceID from 'utils/print/getInsuranceID';

import getDiagnosisCode from 'utils/print/getDiagnosisCode';

import getOrderLineHCPCSCode from 'utils/print/getOrderLineHCPCSCode';
import getOrderLineDescription from 'utils/print/getOrderLineDescription';
import getOrderLineUnits from 'utils/print/getOrderLineUnits';
import getOrderLineRefill from 'utils/print/getOrderLineRefill';

import getPrescriberName from 'utils/print/getPrescriberName';
import getPrescriberNPI from 'utils/print/getPrescriberNPI';
import getPrescriberLIC from 'utils/print/getPrescriberLIC';
import getPrescriberStreet from 'utils/print/getPrescriberStreet';
import getPrescriberCityStateZip from 'utils/print/getPrescriberCityStateZip';

import getReferralSourceName from 'utils/print/getReferralSourceName';

import getOrderLineDosFrom from 'utils/print/getOrderLineDosFrom';

const getInsuranceLine = (props) => {
  const type = getInsurancePlanAndType(props);
  const name = getInsuranceName(props);
  if (type && name) {
    return `${type} - ${name}`;
  }
  return name || type || null;
};

const ORDER_ITEMS_TOP = 85;
const ORDER_ITEMS_ROW_HEIGHT = 18.7;

const PrescriptionForm = {
  ID: 53,
  Name: 'Prescription Form',
  Type: 'General',
  Format: 'letter',
  Orientation: 'p',
  OrderLineMax: 6,
  Pages: [
    {
      ID: 1,
      imageUrl: 'assets/documents/Prescription-Form/1.js',
      texts: [
        {
          name: 'Business Name',
          getText: props => getFacilityName(props) || getBusinessName(props),
          x: 158,
          y: 22,
          fontWeight: 'bold',
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'Business Street',
          getText: props => getFacilityStreet(props) || getBusinessStreet(props),
          x: 158,
          y: 26,
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'Business City State Zip',
          getText: props => getFacilityCityStateZip(props) || getBusinessCityStateZip(props),
          x: 158,
          y: 30,
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'Order Date',
          getText: () => 'Order Date:',
          x: 172,
          y: 73,
          width: 15,
        },
        {
          name: 'Certify',
          getText: () => `I certify that I am the treating clinician identified on this document. I reviewed the written order, and the information reflects the patient’s condition and treatment
plan. Any statement on my letterhead attached hereto, has been reviewed and signed by me. I certify that the medical necessity information above contained
herein is true, accurate and complete, to the best of my knowledge, and is consistent with the information contained in my clinical records for the Patient and/or in
the Patient's permanent medical record. I certify that the patient/caregiver is capable and has successfully completed training or will be trained on the proper use
of the products prescribed on this written order. I understand that any falsification, omission, or concealment of material fact in that section with respect to any
information contained herein may subject me to civil or criminal liability.`,
          x: 11,
          y: 212,
          width: 225,
          fontSize: 8,
          lineHeight: 1.2,
        },
        {
          name: 'Prescriber Info',
          getText: (props) => {
            const name = getPrescriberName(props);
            const npi = getPrescriberNPI(props);
            const lic = getPrescriberLIC(props);
            return [
              name,
              npi ? `NPI: ${npi}` : null,
              lic ? `LIC: ${lic}` : null,
            ].filter(item => item).join(' | ');
          },
          x: 11,
          y: 237,
          width: 125,
          fontWeight: 'bold',
          fontSize: 10,
          lineHeight: 1.2,
        },
      ],
      fields: [
        {
          name: 'Referral Source Name',
          type: 'text',
          getValue: (props) => {
            if (props.orderLine && props.orderLine.length > 0) {
              return props.referralSource && props.referralSource.ID
                ? getReferralSourceName(props)
                : getPrescriberName(props);
            }
            return getReferralSourceName(props) || getPrescriberName(props);
          },
          x: 90,
          y: 21,
          width: 65,
        },
        {
          name: 'Referral Source Street',
          type: 'text',
          getValue: (props) => {
            if (props.orderLine && props.orderLine.length > 0
              && (!props.referralSource || !props.referralSource.ID)) {
              return getPrescriberStreet(props);
            }
            return !getReferralSourceName(props) ? getPrescriberStreet(props) : '';
          },
          x: 90,
          y: 25,
          width: 65,
        },
        {
          name: 'Referral Source City State Zip',
          type: 'text',
          getValue: (props) => {
            if (props.orderLine && props.orderLine.length > 0
              && (!props.referralSource || !props.referralSource.ID)) {
              return getPrescriberCityStateZip(props);
            }
            return !getReferralSourceName(props) ? getPrescriberCityStateZip(props) : '';
          },
          x: 90,
          y: 29,
          width: 65,
        },
        {
          name: 'Patient First Name',
          type: 'text',
          getValue: getPatientFirstName,
          x: 11,
          y: 49,
          width: 80,
        },
        {
          name: 'Patient Last Name',
          type: 'text',
          getValue: getPatientLastName,
          x: 11,
          y: 54,
          width: 80,
        },
        {
          name: 'Date of Birth',
          type: 'text',
          getValue: getPatientFullDob,
          x: 104,
          y: 54,
          width: 16,
          normalize: normalize.date,
        },
        {
          name: 'Gender',
          type: 'text',
          getValue: getPatientGender,
          x: 137,
          y: 54,
          width: 5,
        },
        {
          name: 'Height',
          type: 'text',
          getValue: getPatientHeightFtIn,
          x: 163,
          y: 54,
          width: 17,
        },
        {
          name: 'Weight',
          type: 'text',
          getValue: props => getPatientWeight({ ...props, showUnits: true }),
          x: 190,
          y: 54,
          width: 16,
        },
        {
          name: 'Billing Address',
          type: 'text',
          getValue: props => getPatientFullAddress({ ...props, billing: true }),
          x: 11,
          y: 74,
          width: 57,
          height: 17,
          multiline: true,
        },
        {
          name: 'Delivery Address',
          type: 'text',
          getValue: getPatientFullAddress,
          x: 11,
          y: 95,
          width: 57,
          height: 17,
          multiline: true,
        },
        {
          name: 'Insurance',
          type: 'text',
          getValue: getInsuranceLine,
          x: 11,
          y: 117,
          width: 57,
          height: 9,
          multiline: true,
        },
        {
          name: 'Insurance ID',
          type: 'text',
          getValue: getInsuranceID,
          x: 11,
          y: 125.5,
          width: 57,
        },
        {
          name: 'Diagnosis',
          type: 'text',
          getValue: props => getDiagnosisCode({ ...props, showDescription: true }),
          x: 11,
          y: 140,
          width: 57,
          height: 65,
          multiline: true,
        },
        {
          name: 'Order Date',
          type: 'text',
          getValue: props => getOrderLineDosFrom({ ...props, index: 0 }),
          x: 189,
          y: 73,
          width: 18,
          textAlign: 'right',
          normalize: normalize.date,
        },
        ...[0, 1, 2, 3, 4, 5].reduce((accumulator, currentValue) => {
          const y = ORDER_ITEMS_TOP + (currentValue * ORDER_ITEMS_ROW_HEIGHT);
          return [
            ...accumulator,
            {
              name: `Order Line ${currentValue} Description`,
              type: 'text',
              getValue: (props) => {
                const description = getOrderLineDescription({ ...props, index: currentValue });
                const hcpcs = getOrderLineHCPCSCode({ ...props, index: currentValue });
                if (description && hcpcs) {
                  return `${description}, ${hcpcs}`;
                }
                return description || hcpcs || null;
              },
              x: 80,
              y,
              width: 90,
              height: 9,
              multiline: true,
            },
            {
              name: `Order Line ${currentValue} Length Of Need`,
              type: 'text',
              getValue: (props) => {
                return props.orderLine && props.orderLine.length > currentValue ? 'Length Of Need:' : null;
              },
              x: 80,
              y: y + 9,
              width: 27,
            },
            {
              name: `Order Line ${currentValue} Refill`,
              type: 'text',
              getValue: props => getOrderLineRefill({ ...props, index: currentValue, showUnits: true }),
              x: 108,
              y: y + 9,
              width: 30,
            },
            {
              name: `Order Line ${currentValue} Total`,
              type: 'text',
              getValue: (props) => {
                return props.orderLine && props.orderLine.length > currentValue ? 'Total:' : null;
              },
              x: 187,
              y,
              width: 11,
              textAlign: 'right',
            },
            {
              name: `Order Line ${currentValue} Units`,
              type: 'text',
              getValue: props => getOrderLineUnits({ ...props, index: currentValue }),
              x: 199,
              y,
              width: 8,
              textAlign: 'right',
            },
          ];
        }, []),
        {
          name: 'Refill',
          type: 'text',
          x: 100,
          y: 197.5,
          width: 45,
          normalize: normalize.positiveNumberWithZero,
        },
        {
          name: 'Clinician Signature',
          type: 'signature',
          x: 12,
          y: 243,
          width: 130,
          height: 12,
        },
      ],
    },
  ],
};

export default PrescriptionForm;



// WEBPACK FOOTER //
// ./src/config/documents/Prescription-Form.js